import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  PlayIcon,
  PauseIcon,
  ArrowPathIcon,
  FireIcon,
  BoltIcon,
  SparklesIcon,
  ChartBarIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/solid';
import CoordinateGrid, { GridPoint } from '../lesson/CoordinateGrid';

// Question Types
type QuestionType =
  | 'coordinate_click'
  | 'angle_slider'
  | 'drag_order'
  | 'area_calculate'
  | 'fraction_visual'
  | 'equation_balance'
  | 'multiple_choice';

interface BaseQuestion {
  id: string;
  difficulty: number;
  type: QuestionType;
  concept: string;
  instruction: string;
}

interface CoordinateQuestion extends BaseQuestion {
  type: 'coordinate_click';
  targetPoint: { x: number; y: number };
  gridSize: number;
}

interface AngleSliderQuestion extends BaseQuestion {
  type: 'angle_slider';
  targetAngle: number;
  tolerance: number;
  showTriangle: boolean;
}

interface DragOrderQuestion extends BaseQuestion {
  type: 'drag_order';
  items: string[];
  correctOrder: number[];
}

interface AreaCalculateQuestion extends BaseQuestion {
  type: 'area_calculate';
  shape: 'rectangle' | 'triangle' | 'circle';
  dimensions: { width?: number; height?: number; base?: number; radius?: number };
  correctAnswer: number;
  options: number[];
}

interface FractionVisualQuestion extends BaseQuestion {
  type: 'fraction_visual';
  totalParts: number;
  shadedParts: number;
  correctFraction: string;
  options: string[];
}

interface EquationBalanceQuestion extends BaseQuestion {
  type: 'equation_balance';
  equation: string;
  steps: { instruction: string; correctValue: number; options: number[] }[];
}

interface MultipleChoiceQuestion extends BaseQuestion {
  type: 'multiple_choice';
  equation: string;
  correctAnswer: number;
  options: number[];
}

type Question = CoordinateQuestion | AngleSliderQuestion | DragOrderQuestion |
  AreaCalculateQuestion | FractionVisualQuestion | EquationBalanceQuestion | MultipleChoiceQuestion;

// ===== CONCEPT-GROUPED QUESTION BANK =====

interface ConceptGroup {
  name: string;
  color: string;
  icon: string;
  questions: Question[];
  /** Pre-defined simulation outcomes for this concept */
  simPattern: boolean[];
}

const conceptGroups: ConceptGroup[] = [
  {
    name: 'Algebra',
    color: '#8b5cf6',
    icon: '𝑥',
    simPattern: [true, true, true, false, true],
    questions: [
      {
        id: 'alg1', difficulty: 2, type: 'multiple_choice', concept: 'Algebra',
        instruction: 'Simplify: 3x + 2x',
        equation: '3x + 2x = ?', correctAnswer: 5, options: [5, 6, 32, 1],
      },
      {
        id: 'alg2', difficulty: 3, type: 'multiple_choice', concept: 'Algebra',
        instruction: 'Solve for x',
        equation: '2x = 10', correctAnswer: 5, options: [5, 20, 8, 12],
      },
      {
        id: 'alg3', difficulty: 4, type: 'multiple_choice', concept: 'Algebra',
        instruction: 'Solve for x',
        equation: '3x + 5 = 20', correctAnswer: 5, options: [5, 15, 8, 3],
      },
      {
        id: 'alg4', difficulty: 5, type: 'multiple_choice', concept: 'Algebra',
        instruction: 'Solve for x',
        equation: '5x - 3 = 2x + 12', correctAnswer: 5, options: [5, 3, 9, 15],
      },
      {
        id: 'alg5', difficulty: 6, type: 'multiple_choice', concept: 'Algebra',
        instruction: 'Solve for x',
        equation: '3(x - 2) = 2(x + 4)', correctAnswer: 14, options: [14, 2, 10, 6],
      },
    ],
  },
  {
    name: 'Geometry',
    color: '#0ea5e9',
    icon: '△',
    simPattern: [true, true, false, true, true],
    questions: [
      {
        id: 'geo1', difficulty: 2, type: 'coordinate_click', concept: 'Geometry',
        instruction: 'Click on the point (3, 4)',
        targetPoint: { x: 3, y: 4 }, gridSize: 6,
      },
      {
        id: 'geo2', difficulty: 3, type: 'angle_slider', concept: 'Geometry',
        instruction: 'Adjust the slider to make a 45° angle',
        targetAngle: 45, tolerance: 3, showTriangle: true,
      },
      {
        id: 'geo3', difficulty: 4, type: 'area_calculate', concept: 'Geometry',
        instruction: 'Calculate the area of this rectangle',
        shape: 'rectangle', dimensions: { width: 6, height: 4 },
        correctAnswer: 24, options: [24, 20, 10, 28],
      },
      {
        id: 'geo4', difficulty: 5, type: 'coordinate_click', concept: 'Geometry',
        instruction: 'Click on the point (-2, 3)',
        targetPoint: { x: -2, y: 3 }, gridSize: 6,
      },
      {
        id: 'geo5', difficulty: 6, type: 'area_calculate', concept: 'Geometry',
        instruction: 'Calculate the area of this triangle',
        shape: 'triangle', dimensions: { base: 8, height: 5 },
        correctAnswer: 20, options: [20, 40, 13, 25],
      },
    ],
  },
  {
    name: 'Multi-step Equations',
    color: '#f59e0b',
    icon: '⟹',
    simPattern: [true, false, true, true, true],
    questions: [
      {
        id: 'ms1', difficulty: 3, type: 'equation_balance', concept: 'Multi-step Equations',
        instruction: 'Solve step by step: 2x + 4 = 12',
        equation: '2x + 4 = 12',
        steps: [
          { instruction: 'Step 1: Subtract 4 from both sides. 2x = ?', correctValue: 8, options: [8, 16, 4, 12] },
          { instruction: 'Step 2: Divide both sides by 2. x = ?', correctValue: 4, options: [4, 16, 2, 6] },
        ],
      },
      {
        id: 'ms2', difficulty: 4, type: 'equation_balance', concept: 'Multi-step Equations',
        instruction: 'Solve step by step: 3x + 7 = 22',
        equation: '3x + 7 = 22',
        steps: [
          { instruction: 'Step 1: Subtract 7 from both sides. 3x = ?', correctValue: 15, options: [15, 29, 7, 22] },
          { instruction: 'Step 2: Divide both sides by 3. x = ?', correctValue: 5, options: [5, 45, 3, 12] },
        ],
      },
      {
        id: 'ms3', difficulty: 5, type: 'equation_balance', concept: 'Multi-step Equations',
        instruction: 'Solve step by step: 2(x + 3) = 16',
        equation: '2(x + 3) = 16',
        steps: [
          { instruction: 'Step 1: Divide both sides by 2. x + 3 = ?', correctValue: 8, options: [8, 32, 13, 5] },
          { instruction: 'Step 2: Subtract 3 from both sides. x = ?', correctValue: 5, options: [5, 11, 3, 8] },
        ],
      },
      {
        id: 'ms4', difficulty: 6, type: 'drag_order', concept: 'Multi-step Equations',
        instruction: 'Arrange the steps to solve: 2x + 6 = 14',
        items: ['x = 4', '2x = 8', '2x + 6 = 14', '2x = 14 - 6'],
        correctOrder: [2, 3, 1, 0],
      },
      {
        id: 'ms5', difficulty: 7, type: 'equation_balance', concept: 'Multi-step Equations',
        instruction: 'Solve: 4x - 5 = 2x + 9',
        equation: '4x - 5 = 2x + 9',
        steps: [
          { instruction: 'Step 1: Subtract 2x from both sides. 2x - 5 = ?', correctValue: 9, options: [9, 14, 4, 7] },
          { instruction: 'Step 2: Add 5 to both sides. 2x = ?', correctValue: 14, options: [14, 4, 9, 18] },
          { instruction: 'Step 3: Divide by 2. x = ?', correctValue: 7, options: [7, 28, 14, 4] },
        ],
      },
    ],
  },
  {
    name: 'Solving Equations',
    color: '#10b981',
    icon: '=',
    simPattern: [true, true, true, true, false],
    questions: [
      {
        id: 'se1', difficulty: 2, type: 'multiple_choice', concept: 'Solving Equations',
        instruction: 'Solve for x',
        equation: 'x + 7 = 15', correctAnswer: 8, options: [8, 22, 7, 15],
      },
      {
        id: 'se2', difficulty: 3, type: 'multiple_choice', concept: 'Solving Equations',
        instruction: 'Solve for x',
        equation: '4x = 24', correctAnswer: 6, options: [6, 28, 20, 96],
      },
      {
        id: 'se3', difficulty: 4, type: 'fraction_visual', concept: 'Solving Equations',
        instruction: 'What fraction of the equation is solved?',
        totalParts: 4, shadedParts: 3,
        correctFraction: '3/4', options: ['3/4', '1/4', '4/3', '1/3'],
      },
      {
        id: 'se4', difficulty: 5, type: 'drag_order', concept: 'Solving Equations',
        instruction: 'Arrange in order: 3 + 4 × 2 - 1',
        items: ['= 10', '= 3 + 8 - 1', '= 11 - 1', '3 + 4 × 2 - 1'],
        correctOrder: [3, 1, 2, 0],
      },
      {
        id: 'se5', difficulty: 6, type: 'multiple_choice', concept: 'Solving Equations',
        instruction: 'Solve for x',
        equation: '2(x + 3) - 4 = 12', correctAnswer: 5, options: [5, 8, 3, 11],
      },
    ],
  },
];

const DIFFICULTY_COLORS = {
  easy: '#10b981',
  medium: '#f59e0b',
  hard: '#ef4444',
};

const getDifficultyLabel = (d: number): { label: string; color: string } => {
  if (d <= 3) return { label: 'Easy', color: DIFFICULTY_COLORS.easy };
  if (d <= 6) return { label: 'Medium', color: DIFFICULTY_COLORS.medium };
  return { label: 'Hard', color: DIFFICULTY_COLORS.hard };
};

// ===== INTERACTIVE COMPONENTS =====

// Angle Slider Component
const AngleSlider: React.FC<{
  targetAngle: number;
  value: number;
  onChange: (value: number) => void;
  showResult: boolean;
  isCorrect: boolean;
}> = ({ value, onChange, showResult, isCorrect }) => {
  const svgSize = 200;
  const center = svgSize / 2;
  const radius = 70;

  const angleRad = (value * Math.PI) / 180;
  const endX = center + radius * Math.cos(-angleRad + Math.PI);
  const endY = center + radius * Math.sin(-angleRad + Math.PI);

  return (
    <div className="flex flex-col items-center gap-4">
      <svg width={svgSize} height={svgSize / 2 + 30} className="bg-[#0f172a] rounded-xl">
        <line x1={center - radius} y1={center} x2={center + radius} y2={center} stroke="#64748b" strokeWidth={3} />
        <path
          d={`M ${center + 30} ${center} A 30 30 0 0 0 ${center + 30 * Math.cos(-angleRad + Math.PI)} ${center + 30 * Math.sin(-angleRad + Math.PI)}`}
          fill="none"
          stroke={showResult ? (isCorrect ? '#10b981' : '#ef4444') : '#0ea5e9'}
          strokeWidth={3}
        />
        <line
          x1={center} y1={center} x2={endX} y2={endY}
          stroke={showResult ? (isCorrect ? '#10b981' : '#ef4444') : '#0ea5e9'}
          strokeWidth={3} strokeLinecap="round"
        />
        <text x={center} y={center - 50} textAnchor="middle" fill="white" fontSize="24" fontWeight="bold">
          {value}°
        </text>
        <circle cx={center} cy={center} r={5} fill="#f59e0b" />
      </svg>
      <input
        type="range" min={0} max={180} value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        disabled={showResult} className="w-48 accent-[#0ea5e9]"
      />
    </div>
  );
};

// Drag Order Component
const DragOrder: React.FC<{
  items: string[];
  currentOrder: number[];
  onReorder: (newOrder: number[]) => void;
  correctOrder: number[];
  showResult: boolean;
}> = ({ items, currentOrder, onReorder, correctOrder, showResult }) => {
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  const handleDragStart = (index: number) => {
    if (showResult) return;
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index || showResult) return;
    const newOrder = [...currentOrder];
    const draggedItem = newOrder[draggedIndex];
    newOrder.splice(draggedIndex, 1);
    newOrder.splice(index, 0, draggedItem);
    onReorder(newOrder);
    setDraggedIndex(index);
  };

  return (
    <div className="flex flex-col gap-2 w-full max-w-sm">
      {currentOrder.map((itemIndex, position) => {
        const isCorrectPosition = showResult && correctOrder[position] === itemIndex;
        return (
          <div
            key={itemIndex}
            draggable={!showResult}
            onDragStart={() => handleDragStart(position)}
            onDragOver={(e) => handleDragOver(e, position)}
            onDragEnd={() => setDraggedIndex(null)}
            className={`p-3 rounded-lg font-mono text-sm cursor-grab active:cursor-grabbing transition-all ${
              showResult
                ? isCorrectPosition
                  ? 'bg-[#10b981]/20 border-2 border-[#10b981]'
                  : 'bg-[#ef4444]/20 border-2 border-[#ef4444]'
                : 'bg-[#1e293b] border-2 border-[#334155] hover:border-[#0ea5e9]'
            }`}
          >
            <span className="text-[#64748b] mr-2">{position + 1}.</span>
            <span className="text-white">{items[itemIndex]}</span>
          </div>
        );
      })}
    </div>
  );
};

// Shape Area Component
const ShapeArea: React.FC<{
  shape: 'rectangle' | 'triangle' | 'circle';
  dimensions: { width?: number; height?: number; base?: number; radius?: number };
}> = ({ shape, dimensions }) => {
  const svgSize = 180;
  return (
    <svg width={svgSize} height={svgSize} className="bg-[#0f172a] rounded-xl">
      {shape === 'rectangle' && (
        <>
          <rect x={30} y={40} width={120} height={80} fill="#0ea5e9" fillOpacity={0.3} stroke="#0ea5e9" strokeWidth={2} />
          <text x={90} y={150} textAnchor="middle" fill="#94a3b8" fontSize="12">{dimensions.width} cm</text>
          <text x={165} y={85} textAnchor="middle" fill="#94a3b8" fontSize="12" transform="rotate(90, 165, 85)">{dimensions.height} cm</text>
        </>
      )}
      {shape === 'triangle' && (
        <>
          <polygon points="90,30 30,140 150,140" fill="#10b981" fillOpacity={0.3} stroke="#10b981" strokeWidth={2} />
          <text x={90} y={160} textAnchor="middle" fill="#94a3b8" fontSize="12">base = {dimensions.base} cm</text>
          <line x1={90} y1={30} x2={90} y2={140} stroke="#f59e0b" strokeWidth={1} strokeDasharray="4 2" />
          <text x={100} y={90} fill="#f59e0b" fontSize="10">h={dimensions.height}</text>
        </>
      )}
      {shape === 'circle' && (
        <>
          <circle cx={90} cy={90} r={60} fill="#8b5cf6" fillOpacity={0.3} stroke="#8b5cf6" strokeWidth={2} />
          <line x1={90} y1={90} x2={150} y2={90} stroke="#f59e0b" strokeWidth={2} />
          <text x={120} y={80} fill="#f59e0b" fontSize="12">r = {dimensions.radius}</text>
        </>
      )}
    </svg>
  );
};

// Fraction Visual Component
const FractionVisual: React.FC<{
  totalParts: number;
  shadedParts: number;
}> = ({ totalParts, shadedParts }) => {
  const svgSize = 180;
  const radius = 70;
  const center = svgSize / 2;

  return (
    <svg width={svgSize} height={svgSize} className="bg-[#0f172a] rounded-xl">
      {Array.from({ length: totalParts }, (_, i) => {
        const startAngle = (i * 360) / totalParts - 90;
        const endAngle = ((i + 1) * 360) / totalParts - 90;
        const startRad = (startAngle * Math.PI) / 180;
        const endRad = (endAngle * Math.PI) / 180;
        const x1 = center + radius * Math.cos(startRad);
        const y1 = center + radius * Math.sin(startRad);
        const x2 = center + radius * Math.cos(endRad);
        const y2 = center + radius * Math.sin(endRad);
        const largeArc = endAngle - startAngle > 180 ? 1 : 0;
        return (
          <path
            key={i}
            d={`M ${center} ${center} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`}
            fill={i < shadedParts ? '#0ea5e9' : 'transparent'}
            stroke="#334155" strokeWidth={2}
            fillOpacity={i < shadedParts ? 0.6 : 0}
          />
        );
      })}
    </svg>
  );
};

// ===== MAIN COMPONENT =====
const TeacherSlide07ZPDDemo: React.FC = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentDifficulty, setCurrentDifficulty] = useState(3);
  const [currentQuestion, setCurrentQuestion] = useState<Question | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [streak, setStreak] = useState(0);
  const [xp, setXp] = useState(0);
  const [questionsAnswered, setQuestionsAnswered] = useState(0);
  const [correctCount, setCorrectCount] = useState(0);
  const [difficultyHistory, setDifficultyHistory] = useState<number[]>([3]);
  const [comboMultiplier, setComboMultiplier] = useState(1);
  const [celebrationEffect, setCelebrationEffect] = useState<string | null>(null);

  // Concept tracking
  const [currentConceptIndex, setCurrentConceptIndex] = useState(0);
  const [questionInConcept, setQuestionInConcept] = useState(0);
  const [completedConcepts, setCompletedConcepts] = useState<number[]>([]);
  const [conceptTransition, setConceptTransition] = useState(false);

  // Question-specific state
  const [selectedPoint, setSelectedPoint] = useState<{ x: number; y: number } | null>(null);
  const [angleValue, setAngleValue] = useState(45);
  const [dragOrder, setDragOrder] = useState<number[]>([]);
  const [selectedOption, setSelectedOption] = useState<number | string | null>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [stepAnswers, setStepAnswers] = useState<number[]>([]);

  const [simPhase, setSimPhase] = useState<'idle' | 'showing' | 'interacting' | 'result' | 'concept_transition'>('idle');
  const simIndex = useRef(0);

  const currentConcept = conceptGroups[currentConceptIndex];

  const getQuestionForConcept = useCallback((conceptIdx: number, targetDiff: number): Question => {
    const group = conceptGroups[conceptIdx];
    // Find the question closest to the target difficulty within the concept
    const sorted = [...group.questions].sort(
      (a, b) => Math.abs(a.difficulty - targetDiff) - Math.abs(b.difficulty - targetDiff)
    );
    return sorted[0];
  }, []);

  const resetQuestionState = useCallback(() => {
    setSelectedPoint(null);
    setAngleValue(45);
    setDragOrder([]);
    setSelectedOption(null);
    setCurrentStep(0);
    setStepAnswers([]);
  }, []);

  const showQuestion = useCallback(() => {
    const question = getQuestionForConcept(currentConceptIndex, currentDifficulty);
    setCurrentQuestion(question);
    resetQuestionState();
    setShowResult(false);

    if (question.type === 'drag_order') {
      setDragOrder(Array.from({ length: question.items.length }, (_, i) => i));
    }
  }, [currentConceptIndex, currentDifficulty, getQuestionForConcept, resetQuestionState]);

  const simulateInteraction = useCallback((shouldBeCorrect: boolean) => {
    if (!currentQuestion) return;

    switch (currentQuestion.type) {
      case 'coordinate_click':
        if (shouldBeCorrect) {
          setSelectedPoint(currentQuestion.targetPoint);
        } else {
          setSelectedPoint({
            x: currentQuestion.targetPoint.x + (Math.random() > 0.5 ? 1 : -1),
            y: currentQuestion.targetPoint.y + (Math.random() > 0.5 ? 1 : -1)
          });
        }
        break;
      case 'angle_slider':
        setAngleValue(shouldBeCorrect ? currentQuestion.targetAngle : currentQuestion.targetAngle + (Math.random() > 0.5 ? 15 : -15));
        break;
      case 'drag_order':
        if (shouldBeCorrect) {
          setDragOrder(currentQuestion.correctOrder);
        } else {
          const wrongOrder = [...currentQuestion.correctOrder];
          [wrongOrder[0], wrongOrder[1]] = [wrongOrder[1], wrongOrder[0]];
          setDragOrder(wrongOrder);
        }
        break;
      case 'area_calculate':
        setSelectedOption(shouldBeCorrect ? currentQuestion.correctAnswer : currentQuestion.options[1]);
        break;
      case 'fraction_visual':
        setSelectedOption(shouldBeCorrect ? currentQuestion.correctFraction : currentQuestion.options[1]);
        break;
      case 'equation_balance': {
        const answers = currentQuestion.steps.map((step) =>
          shouldBeCorrect ? step.correctValue : step.options[1]
        );
        setStepAnswers(answers);
        setCurrentStep(currentQuestion.steps.length);
        break;
      }
      case 'multiple_choice':
        setSelectedOption(shouldBeCorrect ? currentQuestion.correctAnswer : currentQuestion.options[1]);
        break;
    }
  }, [currentQuestion]);

  const processResult = useCallback((correct: boolean) => {
    setShowResult(true);
    setIsCorrect(correct);
    setQuestionsAnswered(prev => prev + 1);

    if (correct) {
      setCorrectCount(prev => prev + 1);
      const newStreak = streak + 1;
      setStreak(newStreak);
      const newMultiplier = Math.min(1 + Math.floor(newStreak / 3) * 0.5, 3);
      setComboMultiplier(newMultiplier);

      if (currentQuestion) {
        const baseXP = currentQuestion.difficulty * 10;
        setXp(prev => prev + Math.round(baseXP * newMultiplier));
      }

      if (newStreak === 3) setCelebrationEffect('Nice streak!');
      else if (newStreak === 5) setCelebrationEffect('On Fire!');
      else if (newStreak >= 7) setCelebrationEffect('Unstoppable!');

      if (newStreak >= 2 && currentDifficulty < 10) {
        const newDiff = Math.min(currentDifficulty + 1, 10);
        setCurrentDifficulty(newDiff);
        setDifficultyHistory(prev => [...prev, newDiff]);
      } else {
        setDifficultyHistory(prev => [...prev, currentDifficulty]);
      }
    } else {
      setStreak(0);
      setComboMultiplier(1);
      if (currentDifficulty > 1) {
        const newDiff = Math.max(currentDifficulty - 1, 1);
        setCurrentDifficulty(newDiff);
        setDifficultyHistory(prev => [...prev, newDiff]);
      } else {
        setDifficultyHistory(prev => [...prev, currentDifficulty]);
      }
    }

    setTimeout(() => setCelebrationEffect(null), 1500);
  }, [streak, currentDifficulty, currentQuestion]);

  // Simulation loop
  useEffect(() => {
    if (!isPlaying) return;

    if (simPhase === 'idle') {
      showQuestion();
      setSimPhase('showing');
      return;
    }

    if (simPhase === 'showing' && currentQuestion) {
      const timer = setTimeout(() => setSimPhase('interacting'), 800);
      return () => clearTimeout(timer);
    }

    if (simPhase === 'interacting' && currentQuestion) {
      const concept = conceptGroups[currentConceptIndex];
      const patternIdx = questionInConcept % concept.simPattern.length;
      const shouldBeCorrect = concept.simPattern[patternIdx];

      const delay = shouldBeCorrect ? 1800 + Math.random() * 800 : 2500 + Math.random() * 1000;

      const timer = setTimeout(() => {
        simulateInteraction(shouldBeCorrect);
        setTimeout(() => {
          processResult(shouldBeCorrect);
          simIndex.current++;
          setSimPhase('result');
        }, 500);
      }, delay);

      return () => clearTimeout(timer);
    }

    if (simPhase === 'result') {
      const timer = setTimeout(() => {
        const nextQ = questionInConcept + 1;
        const questionsInThisConcept = conceptGroups[currentConceptIndex].questions.length;

        if (nextQ >= questionsInThisConcept) {
          // Concept complete - transition
          setCompletedConcepts(prev => [...prev, currentConceptIndex]);
          setQuestionInConcept(0);

          if (currentConceptIndex < conceptGroups.length - 1) {
            setConceptTransition(true);
            setSimPhase('concept_transition');
          } else {
            // All concepts done
            setIsPlaying(false);
            setCelebrationEffect('All concepts mastered!');
            setTimeout(() => setCelebrationEffect(null), 3000);
          }
        } else {
          setQuestionInConcept(nextQ);
          showQuestion();
          setSimPhase('showing');
        }
      }, 2000);
      return () => clearTimeout(timer);
    }

    if (simPhase === 'concept_transition') {
      const timer = setTimeout(() => {
        setCurrentConceptIndex(prev => prev + 1);
        setConceptTransition(false);
        setCurrentDifficulty(3); // Reset difficulty for new concept
        setStreak(0);
        setComboMultiplier(1);
        showQuestion();
        setSimPhase('showing');
      }, 2500);
      return () => clearTimeout(timer);
    }
  }, [isPlaying, simPhase, currentQuestion, showQuestion, simulateInteraction, processResult, currentConceptIndex, questionInConcept]);

  // Update question when concept changes
  useEffect(() => {
    if (isPlaying && simPhase === 'showing') {
      showQuestion();
    }
  }, [currentConceptIndex]);

  const reset = () => {
    setIsPlaying(false);
    setSimPhase('idle');
    setCurrentDifficulty(3);
    setCurrentQuestion(null);
    resetQuestionState();
    setShowResult(false);
    setStreak(0);
    setXp(0);
    setQuestionsAnswered(0);
    setCorrectCount(0);
    setDifficultyHistory([3]);
    setComboMultiplier(1);
    setCelebrationEffect(null);
    setCurrentConceptIndex(0);
    setQuestionInConcept(0);
    setCompletedConcepts([]);
    setConceptTransition(false);
    simIndex.current = 0;
  };

  const { label: diffLabel, color: diffColor } = getDifficultyLabel(currentDifficulty);
  const zpdMin = Math.max(1, currentDifficulty - 2);
  const zpdMax = Math.min(10, currentDifficulty + 2);

  // Render question content based on type
  const renderQuestion = () => {
    if (conceptTransition) {
      const nextConcept = conceptGroups[currentConceptIndex + 1];
      return (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="flex flex-col items-center gap-6"
        >
          <div className="flex items-center gap-3">
            <CheckCircleIcon className="w-8 h-8 text-[#10b981]" />
            <span className="text-2xl font-bold text-[#10b981]">{currentConcept.name} Complete!</span>
          </div>
          <div className="h-px w-48 bg-gradient-to-r from-transparent via-[#334155] to-transparent" />
          {nextConcept && (
            <div className="flex flex-col items-center gap-2">
              <span className="text-[#94a3b8] text-sm">Next concept:</span>
              <div className="flex items-center gap-3">
                <span className="text-3xl" style={{ color: nextConcept.color }}>{nextConcept.icon}</span>
                <span className="text-xl font-bold text-white">{nextConcept.name}</span>
              </div>
            </div>
          )}
        </motion.div>
      );
    }

    if (!currentQuestion) {
      return <p className="text-[#64748b]">Press Play to start the adaptive learning demo</p>;
    }

    return (
      <div className="flex flex-col items-center gap-4">
        {/* Concept tag */}
        <span
          className="px-3 py-1 rounded-full text-xs font-medium border"
          style={{
            background: `${currentConcept.color}20`,
            color: currentConcept.color,
            borderColor: `${currentConcept.color}30`,
          }}
        >
          {currentConcept.icon} {currentQuestion.concept}
        </span>

        {/* Instruction */}
        <p className="text-white text-lg font-medium text-center">{currentQuestion.instruction}</p>

        {/* Question-specific UI */}
        {currentQuestion.type === 'coordinate_click' && (
          <CoordinateGrid
            size={260}
            gridRange={currentQuestion.gridSize}
            points={[
              ...(selectedPoint ? [{
                id: 'selected', x: selectedPoint.x, y: selectedPoint.y,
                state: showResult ? (isCorrect ? 'correct' : 'incorrect') : undefined,
              } as GridPoint] : []),
              ...(showResult && !isCorrect ? [{
                id: 'correct', x: currentQuestion.targetPoint.x, y: currentQuestion.targetPoint.y,
                state: 'correct' as const,
              }] : []),
            ]}
            onGridClick={(pos) => !showResult && setSelectedPoint(pos)}
            allowPlacement={!showResult}
            disabled={showResult}
          />
        )}

        {currentQuestion.type === 'angle_slider' && (
          <AngleSlider
            targetAngle={currentQuestion.targetAngle}
            value={angleValue}
            onChange={setAngleValue}
            showResult={showResult}
            isCorrect={isCorrect}
          />
        )}

        {currentQuestion.type === 'drag_order' && (
          <DragOrder
            items={currentQuestion.items}
            currentOrder={dragOrder}
            onReorder={setDragOrder}
            correctOrder={currentQuestion.correctOrder}
            showResult={showResult}
          />
        )}

        {currentQuestion.type === 'area_calculate' && (
          <div className="flex flex-col items-center gap-4">
            <ShapeArea shape={currentQuestion.shape} dimensions={currentQuestion.dimensions} />
            <div className="grid grid-cols-2 gap-2">
              {currentQuestion.options.map((opt) => (
                <button
                  key={opt}
                  onClick={() => !showResult && setSelectedOption(opt)}
                  className={`px-4 py-2 rounded-lg font-bold transition-all ${
                    selectedOption === opt
                      ? showResult
                        ? opt === currentQuestion.correctAnswer
                          ? 'bg-[#10b981]/30 border-2 border-[#10b981]'
                          : 'bg-[#ef4444]/30 border-2 border-[#ef4444]'
                        : 'bg-[#0ea5e9]/30 border-2 border-[#0ea5e9]'
                      : 'bg-[#1e293b] border-2 border-[#334155]'
                  } ${showResult && opt === currentQuestion.correctAnswer ? 'bg-[#10b981]/30 border-2 border-[#10b981]' : ''}`}
                >
                  {opt} cm²
                </button>
              ))}
            </div>
          </div>
        )}

        {currentQuestion.type === 'fraction_visual' && (
          <div className="flex flex-col items-center gap-4">
            <FractionVisual totalParts={currentQuestion.totalParts} shadedParts={currentQuestion.shadedParts} />
            <div className="grid grid-cols-2 gap-2">
              {currentQuestion.options.map((opt) => (
                <button
                  key={opt}
                  onClick={() => !showResult && setSelectedOption(opt)}
                  className={`px-4 py-2 rounded-lg font-bold text-lg transition-all ${
                    selectedOption === opt
                      ? showResult
                        ? opt === currentQuestion.correctFraction
                          ? 'bg-[#10b981]/30 border-2 border-[#10b981]'
                          : 'bg-[#ef4444]/30 border-2 border-[#ef4444]'
                        : 'bg-[#0ea5e9]/30 border-2 border-[#0ea5e9]'
                      : 'bg-[#1e293b] border-2 border-[#334155]'
                  } ${showResult && opt === currentQuestion.correctFraction ? 'bg-[#10b981]/30 border-2 border-[#10b981]' : ''}`}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>
        )}

        {currentQuestion.type === 'equation_balance' && (
          <div className="flex flex-col items-center gap-4 w-full max-w-md">
            <div className="text-2xl font-mono font-bold text-white bg-[#1e293b] px-6 py-3 rounded-xl">
              {currentQuestion.equation}
            </div>
            {currentQuestion.steps.map((step, idx) => (
              <div key={idx} className={`w-full p-3 rounded-lg ${idx <= currentStep ? 'opacity-100' : 'opacity-40'}`}>
                <p className="text-sm text-[#94a3b8] mb-2">{step.instruction}</p>
                {idx <= currentStep && (
                  <div className="grid grid-cols-4 gap-2">
                    {step.options.map((opt) => (
                      <button
                        key={opt}
                        onClick={() => {
                          if (showResult || idx !== currentStep) return;
                          const newAnswers = [...stepAnswers];
                          newAnswers[idx] = opt;
                          setStepAnswers(newAnswers);
                          if (idx < currentQuestion.steps.length - 1) setCurrentStep(idx + 1);
                        }}
                        className={`px-3 py-2 rounded font-bold transition-all ${
                          stepAnswers[idx] === opt
                            ? showResult
                              ? opt === step.correctValue
                                ? 'bg-[#10b981]/30 border-2 border-[#10b981]'
                                : 'bg-[#ef4444]/30 border-2 border-[#ef4444]'
                              : 'bg-[#0ea5e9]/30 border-2 border-[#0ea5e9]'
                            : 'bg-[#1e293b] border border-[#334155]'
                        } ${showResult && opt === step.correctValue ? 'bg-[#10b981]/30 border-2 border-[#10b981]' : ''}`}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {currentQuestion.type === 'multiple_choice' && (
          <div className="flex flex-col items-center gap-4">
            <div className="text-3xl font-mono font-bold text-white bg-[#1e293b] px-6 py-4 rounded-xl">
              {currentQuestion.equation}
            </div>
            <div className="grid grid-cols-2 gap-3">
              {currentQuestion.options.map((opt) => (
                <button
                  key={opt}
                  onClick={() => !showResult && setSelectedOption(opt)}
                  className={`px-6 py-3 rounded-xl font-bold text-lg transition-all ${
                    selectedOption === opt
                      ? showResult
                        ? opt === currentQuestion.correctAnswer
                          ? 'bg-[#10b981]/30 border-2 border-[#10b981] text-[#10b981]'
                          : 'bg-[#ef4444]/30 border-2 border-[#ef4444] text-[#ef4444]'
                        : 'bg-[#0ea5e9]/30 border-2 border-[#0ea5e9] text-white'
                      : 'bg-[#1e293b] border-2 border-[#334155] text-white'
                  } ${showResult && opt === currentQuestion.correctAnswer ? 'bg-[#10b981]/30 border-2 border-[#10b981] text-[#10b981]' : ''}`}
                >
                  x = {opt}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Result feedback */}
        <AnimatePresence>
          {showResult && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className={`mt-2 py-2 px-4 rounded-xl ${
                isCorrect ? 'bg-[#10b981]/20 border-2 border-[#10b981]/50' : 'bg-[#ef4444]/20 border-2 border-[#ef4444]/50'
              }`}
            >
              <p className={`font-bold ${isCorrect ? 'text-[#10b981]' : 'text-[#ef4444]'}`}>
                {isCorrect ? `✓ Correct! +${Math.round((currentQuestion?.difficulty || 3) * 10 * comboMultiplier)} XP` : '✗ Not quite - difficulty adjusted down'}
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-6 pt-3 pb-2">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-white">Adaptive Learning in Action</h2>
            <p className="text-xs text-[#94a3b8]">Year 7 Mathematics • Concept-by-concept adaptive progression</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#10b981]/15 border border-[#10b981]/30">
              <SparklesIcon className="w-4 h-4 text-[#10b981]" />
              <span className="text-sm font-bold text-[#10b981]">{xp} XP</span>
            </div>
            {streak > 0 && (
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#f59e0b]/15 border border-[#f59e0b]/30">
                <FireIcon className="w-4 h-4 text-[#f59e0b]" />
                <span className="text-sm font-bold text-[#f59e0b]">{streak}</span>
              </div>
            )}
            {comboMultiplier > 1 && (
              <div className="flex items-center gap-1 px-2 py-1 rounded-lg bg-purple-500/20 border border-purple-500/40">
                <BoltIcon className="w-3.5 h-3.5 text-purple-400" />
                <span className="text-xs font-bold text-purple-400">{comboMultiplier}x</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Concept Progress Bar */}
      <div className="px-6 pb-2">
        <div className="flex items-center gap-2">
          {conceptGroups.map((concept, idx) => {
            const isActive = idx === currentConceptIndex;
            const isComplete = completedConcepts.includes(idx);
            const questionsInConcept = concept.questions.length;
            const progress = isActive ? questionInConcept / questionsInConcept : isComplete ? 1 : 0;

            return (
              <React.Fragment key={idx}>
                {idx > 0 && (
                  <div className={`flex-shrink-0 w-6 h-0.5 ${isComplete || (idx <= currentConceptIndex) ? 'bg-[#334155]' : 'bg-[#1e293b]'}`} />
                )}
                <div className={`flex-1 rounded-lg p-2 transition-all ${
                  isActive ? 'ring-2' : ''
                }`}
                  style={{
                    background: isActive ? `${concept.color}15` : isComplete ? `${concept.color}10` : 'rgba(30, 41, 59, 0.5)',
                    borderColor: isActive ? concept.color : 'transparent',
                    boxShadow: isActive ? `0 0 0 2px ${concept.color}50` : undefined,
                  }}
                >
                  <div className="flex items-center gap-1.5 mb-1">
                    {isComplete && <CheckCircleIcon className="w-3.5 h-3.5 text-[#10b981]" />}
                    <span className={`text-[10px] font-semibold ${isActive ? 'text-white' : isComplete ? 'text-[#94a3b8]' : 'text-[#64748b]'}`}>
                      {concept.name}
                    </span>
                  </div>
                  <div className="h-1 bg-[#1e293b] rounded-full overflow-hidden">
                    <motion.div
                      className="h-full rounded-full"
                      style={{ background: concept.color }}
                      animate={{ width: `${progress * 100}%` }}
                      transition={{ duration: 0.3 }}
                    />
                  </div>
                  {isActive && (
                    <div className="text-[9px] text-[#64748b] mt-0.5">
                      {questionInConcept}/{questionsInConcept}
                    </div>
                  )}
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex gap-4 px-6 pb-2 min-h-0">
        {/* Left: Learning Module */}
        <div className="flex-[2] flex flex-col">
          <div
            className="flex-1 rounded-2xl p-5 flex flex-col relative overflow-hidden"
            style={{
              background: 'linear-gradient(180deg, rgba(15, 23, 42, 0.95) 0%, rgba(10, 15, 30, 0.98) 100%)',
              border: `2px solid ${currentConcept.color}30`,
            }}
          >
            {/* Celebration Effect */}
            <AnimatePresence>
              {celebrationEffect && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 1.2 }}
                  className="absolute inset-0 flex items-center justify-center z-20 pointer-events-none"
                >
                  <div className="text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500">
                    {celebrationEffect}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Difficulty indicator */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="px-3 py-1.5 rounded-lg font-semibold text-sm" style={{ background: `${diffColor}20`, color: diffColor, border: `2px solid ${diffColor}50` }}>
                  Level {currentDifficulty}
                </div>
                <span className="text-sm text-[#94a3b8]">{diffLabel}</span>
              </div>
              <div className="text-sm">
                <span className="text-[#64748b]">Accuracy: </span>
                <span className="font-bold text-white">{questionsAnswered > 0 ? Math.round((correctCount / questionsAnswered) * 100) : 0}%</span>
              </div>
            </div>

            {/* Question area */}
            <div className="flex-1 flex items-center justify-center">
              {renderQuestion()}
            </div>

            {/* Concept progress bar */}
            <div className="mt-auto pt-3">
              <div className="h-2 bg-[#1e293b] rounded-full overflow-hidden">
                <motion.div
                  className="h-full rounded-full"
                  style={{ background: `linear-gradient(90deg, ${currentConcept.color}, ${currentConcept.color}cc)` }}
                  animate={{ width: `${(questionInConcept / currentConcept.questions.length) * 100}%` }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Right: ZPD Visualization */}
        <div className="w-64 flex flex-col gap-3">
          {/* ZPD Meter */}
          <div className="rounded-xl p-4" style={{ background: 'rgba(15, 23, 42, 0.9)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <div className="flex items-center gap-2 mb-4">
              <ChartBarIcon className="w-4 h-4 text-[#0ea5e9]" />
              <span className="text-sm font-semibold text-white">Zone of Proximal Development</span>
            </div>
            <div className="relative h-44 flex flex-col justify-between py-1">
              {[10, 9, 8, 7, 6, 5, 4, 3, 2, 1].map(level => {
                const inZPD = level >= zpdMin && level <= zpdMax;
                const isCurrent = level === currentDifficulty;
                const { color } = getDifficultyLabel(level);
                return (
                  <div key={level} className="flex items-center gap-2 h-3">
                    <span className={`text-[10px] w-4 text-right ${isCurrent ? 'text-white font-bold' : 'text-[#64748b]'}`}>{level}</span>
                    <motion.div
                      className="h-2 rounded-full"
                      style={{
                        background: inZPD ? (isCurrent ? color : `${color}60`) : 'rgba(51, 65, 85, 0.5)',
                        boxShadow: isCurrent ? `0 0 10px ${color}` : undefined,
                      }}
                      animate={{ width: isCurrent ? '100%' : inZPD ? '70%' : '40%' }}
                    />
                  </div>
                );
              })}
            </div>
          </div>

          {/* Difficulty History Graph */}
          <div className="flex-1 rounded-xl p-4" style={{ background: 'rgba(15, 23, 42, 0.9)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <span className="text-sm font-semibold text-white mb-3 block">Difficulty Adaptation</span>
            <div className="h-28 relative">
              {[1, 4, 7, 10].map(level => (
                <div key={level} className="absolute left-0 right-0 border-t border-[#334155]" style={{ bottom: `${((level - 1) / 9) * 100}%` }}>
                  <span className="absolute -left-1 -translate-x-full text-[8px] text-[#64748b] -mt-1.5">{level}</span>
                </div>
              ))}
              {(() => {
                const visibleHistory = difficultyHistory.slice(-15);
                return (
                  <>
                    <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                      <defs>
                        <linearGradient id="lineGradient" x1="0" y1="0" x2="1" y2="0">
                          <stop offset="0%" stopColor={currentConcept.color} />
                          <stop offset="100%" stopColor="#10b981" />
                        </linearGradient>
                      </defs>
                      {visibleHistory.length > 1 && (
                        <>
                          <polygon
                            fill="url(#lineGradient)"
                            opacity="0.15"
                            points={['0,100', ...visibleHistory.map((d, i) => `${(i / Math.max(visibleHistory.length - 1, 1)) * 100},${100 - ((d - 1) / 9) * 100}`), '100,100'].join(' ')}
                          />
                          <polyline
                            fill="none"
                            stroke="url(#lineGradient)"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            vectorEffect="non-scaling-stroke"
                            points={visibleHistory.map((d, i) => `${(i / Math.max(visibleHistory.length - 1, 1)) * 100},${100 - ((d - 1) / 9) * 100}`).join(' ')}
                          />
                        </>
                      )}
                    </svg>
                    {visibleHistory.map((d, i) => {
                      const xPercent = (i / Math.max(visibleHistory.length - 1, 1)) * 100;
                      const yPercent = ((d - 1) / 9) * 100;
                      const isLast = i === visibleHistory.length - 1;
                      return (
                        <div
                          key={i}
                          className="absolute rounded-full"
                          style={{
                            left: `${xPercent}%`,
                            bottom: `${yPercent}%`,
                            width: isLast ? 10 : 5,
                            height: isLast ? 10 : 5,
                            transform: 'translate(-50%, 50%)',
                            background: isLast ? '#10b981' : currentConcept.color,
                            opacity: isLast ? 1 : 0.5,
                            boxShadow: isLast ? '0 0 8px #10b981' : undefined,
                          }}
                        />
                      );
                    })}
                  </>
                );
              })()}
            </div>
            <p className="text-[10px] text-[#64748b] mt-2 text-center">
              {questionsAnswered === 0 ? 'Watching performance...' : isCorrect ? 'Progressing well' : 'Adjusting difficulty'}
            </p>
          </div>

          {/* Stats */}
          <div className="rounded-xl p-3 grid grid-cols-2 gap-2" style={{ background: 'rgba(15, 23, 42, 0.9)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <div className="text-center">
              <div className="text-lg font-bold text-white">{questionsAnswered}</div>
              <div className="text-[10px] text-[#64748b]">Questions</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-[#10b981]">{correctCount}</div>
              <div className="text-[10px] text-[#64748b]">Correct</div>
            </div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="px-6 py-2 flex items-center justify-center gap-4" style={{ background: 'rgba(15,23,42,0.9)', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
        <button
          onClick={() => setIsPlaying(!isPlaying)}
          className="flex items-center gap-1.5 px-5 py-2 rounded-lg font-medium text-sm"
          style={{
            background: isPlaying ? 'rgba(239,68,68,0.2)' : 'rgba(16,185,129,0.2)',
            border: `2px solid ${isPlaying ? 'rgba(239,68,68,0.5)' : 'rgba(16,185,129,0.5)'}`,
            color: isPlaying ? '#ef4444' : '#10b981',
          }}
        >
          {isPlaying ? <><PauseIcon className="w-4 h-4" /> Pause</> : <><PlayIcon className="w-4 h-4" /> {questionsAnswered > 0 ? 'Resume' : 'Start Demo'}</>}
        </button>
        <button onClick={reset} className="flex items-center gap-1 px-3 py-2 rounded-lg text-sm text-[#94a3b8] hover:text-white hover:bg-[#334155]">
          <ArrowPathIcon className="w-4 h-4" /> Reset
        </button>
        <div className="text-sm text-[#64748b]">
          Adaptive difficulty within each concept: {conceptGroups.map(c => c.name).join(' → ')}
        </div>
      </div>
    </div>
  );
};

export default TeacherSlide07ZPDDemo;
